import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { isEqual, isUndefined } from 'lodash';
import {
  DropdownHorizontalAlignmentsEnum,
  DropdownVerticalAlignmentsEnum,
} from '../../../shared/components/dropdown/dropdown.component';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { MOMENT_AM_PM_TIME_FORMAT } from 'i2g-ng-utils';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { CustomFormFieldComponent } from '../custom-form-field/custom-form-field.component';
import { ISelectOption } from '../select-field/select-field.interface';
import { TimePickerFieldBuilder } from './time-picker-field.builder';

// TODO: review time picker, should we just wrap a select-field? should we add text input-field?
@Component({
  selector: 'app-time-picker-field',
  templateUrl: 'time-picker-field.component.html',
  styleUrls: ['time-picker-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [TimePickerFieldBuilder],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerFieldComponent),
      multi: true,
    },
  ],
})
export class TimePickerFieldComponent
  extends CustomFormFieldComponent
  implements ControlValueAccessor, OnInit, OnChanges {
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() description: string;
  @Input() fieldId: string;
  @Input() condensed: boolean;
  @Input() placeholder: string;
  @Input() minDateTime: string;
  @Input() maxDateTime: string;
  @Input() secondsIncrement: number;
  @Input() format: string = MOMENT_AM_PM_TIME_FORMAT;
  @Input() blocker: (selectOption: ISelectOption) => Promise<ISelectOption>;
  @Input() icon: IIcon;
  @Input() e2eId: string;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingTapped: ITrackingAction;
  @Input() trackingSelected: ITrackingAction;
  @Input() trackingAdditionalProperties: any;
  @Input() horizontalAlignment: DropdownHorizontalAlignmentsEnum =
    DropdownHorizontalAlignmentsEnum.left;
  @Input() verticalAlignment: DropdownVerticalAlignmentsEnum =
    DropdownVerticalAlignmentsEnum.bottom;
  @Input() dynamicVAlignment: boolean;
  @Input() formControl: AbstractControl;

  @Output() onClickIcon: EventEmitter<void> = new EventEmitter<void>();

  selectedOption: ISelectOption;
  isDisabled: boolean;
  values: List<ISelectOption>;
  dropdownValues: List<List<ISelectOption>>;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingDispatcher: TrackingDispatcher,
    private _timePickerFieldBuilder: TimePickerFieldBuilder,
    translateService: TranslateService
  ) {
    super(_changeDetectorRef, translateService);
  }

  ngOnChanges(changes) {
    if (
      changes.minDateTime ||
      changes.maxDateTime ||
      changes.secondsIncrement ||
      changes.format
    ) {
      this.values = this._timePickerFieldBuilder.buildValues(
        this.minDateTime,
        this.maxDateTime,
        this.secondsIncrement,
        this.format
      );
      this.dropdownValues = List([this.values]);
    }
  }

  ngOnInit() {
    this.fieldControl = this.formControl;

    super.ngOnInit();
  }

  writeValue(value: any) {
    if (!isUndefined(value) && this.values) {
      let newOption: ISelectOption = this.values.find(
        (selectOption: ISelectOption) => isEqual(value, selectOption.value)
      );

      if (!newOption) {
        newOption = this._timePickerFieldBuilder.buildValue(value, this.format);
      }

      this.setSelectedOption(newOption);
    } else if (this.placeholder) {
      this.setSelectedOption({ key: this.placeholder, value: '' });
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;

    this._changeDetectorRef.markForCheck();
  }

  onSelect(selectOption: ISelectOption) {
    if (
      this.blocker &&
      (isUndefined(selectOption.block) || selectOption.block)
    ) {
      this.blocker(selectOption).then((result: ISelectOption) => {
        if (
          result &&
          (this.selectedOption
            ? !isEqual(this.selectedOption.value, result.value)
            : true)
        ) {
          this.propagateChange(result.value);

          this.setSelectedOption(result);

          this.trackOnSelect(result);
        }
      });
    } else if (
      this.selectedOption
        ? !isEqual(this.selectedOption.key, selectOption.key)
        : true
    ) {
      this.propagateChange(selectOption.value);

      this.setSelectedOption(selectOption);

      this.trackOnSelect(selectOption);
    }
  }

  setSelectedOption(optionValue: ISelectOption) {
    this.selectedOption = optionValue;

    this.formControl.markAsDirty();

    this._changeDetectorRef.markForCheck();
  }

  trackOnSelect(optionValue: ISelectOption) {
    if (
      this.trackingObject &&
      this.trackingSelected &&
      optionValue.trackingValue
    ) {
      this._trackingDispatcher.trackEvent(
        this.trackingObject,
        this.trackingSelected.setValue(optionValue.trackingValue),
        this.trackingAdditionalProperties
      );
    }
  }
}
